import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  CircularProgress,
  FormControlLabel,
  Switch,
  Alert,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { analyzeUrlForEnvironment, EnvironmentInfo } from '../../utils/environmentUtils';
import { DFT_BRAND } from '../../styles/brandPalette';

interface ImportSwaggerFormProps {
  projects: any[];
  /** When true, show loading state in the project dropdown (same pattern as Environments). */
  projectsLoading?: boolean;
  projectId: number | null;
  setProjectId: (id: number) => void;
  swaggerUrl: string;
  setSwaggerUrl: (url: string) => void;
  swaggerFile: File | null;
  setSwaggerFile: (file: File | null) => void;
  /** When provided, form uses controlled "Create environment automatically" from parent (e.g. Swagger page). */
  createEnvironment?: boolean;
  setCreateEnvironment?: (value: boolean) => void;
  onSubmit: () => void;
  error: string | null;
  loading: boolean;
  onPreview?: (environmentInfo: EnvironmentInfo | null) => void;
  hideProjectSelect?: boolean;
}

const ImportSwaggerForm: React.FC<ImportSwaggerFormProps> = ({
  projects,
  projectsLoading = false,
  projectId, 
  setProjectId, 
  swaggerUrl, 
  setSwaggerUrl,
  swaggerFile,
  setSwaggerFile,
  createEnvironment: controlledCreateEnvironment,
  setCreateEnvironment: setControlledCreateEnvironment,
  onSubmit, 
  error, 
  loading,
  onPreview,
  hideProjectSelect = false,
}) => {
  const [internalCreateEnvironment, setInternalCreateEnvironment] = useState(true);
  const createEnvironment = controlledCreateEnvironment !== undefined ? controlledCreateEnvironment : internalCreateEnvironment;
  const setCreateEnvironment = setControlledCreateEnvironment ?? setInternalCreateEnvironment;
  const [environmentInfo, setEnvironmentInfo] = useState<EnvironmentInfo | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);

  // Validate URL must start with http:// or https://; then analyze for environment
  useEffect(() => {
    if (swaggerFile) {
      setEnvironmentInfo(null);
      setUrlError(null);
      onPreview?.(null);
      return;
    }
    if (swaggerUrl && swaggerUrl.trim()) {
      const trimmed = swaggerUrl.trim();
      const validProtocol = /^https?:\/\//i.test(trimmed);
      if (!validProtocol) {
        setEnvironmentInfo(null);
        setUrlError('URL must start with http:// or https://');
        onPreview?.(null);
        return;
      }
      try {
        const info = analyzeUrlForEnvironment(swaggerUrl);
        setEnvironmentInfo(info);
        setUrlError(null);
        onPreview?.(info);
      } catch (error) {
        setEnvironmentInfo(null);
        setUrlError('Invalid URL format');
        onPreview?.(null);
      }
    } else {
      setEnvironmentInfo(null);
      setUrlError(null);
      onPreview?.(null);
    }
  }, [swaggerUrl, swaggerFile, onPreview]);

  const hasFile = swaggerFile != null;
  const hasUrl = Boolean(swaggerUrl && swaggerUrl.trim());
  const canSubmit = projectId != null && (hasFile || hasUrl) && !loading && (!hasUrl || !urlError);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 }, mt: 0 }}>
      {!hideProjectSelect && (
      <TextField
        id="import-swagger-project-select"
        select
        fullWidth
        required
        label="Project"
        disabled={projectsLoading}
        value={projectId != null ? String(projectId) : ''}
        onChange={(e) => {
          const v = (e.target as unknown as HTMLSelectElement).value;
          if (v === '' || v == null) return;
          const num = Number(v);
          if (!Number.isFinite(num)) return;
          setProjectId(num);
        }}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            minHeight: 40,
            '& fieldset': { borderColor: DFT_BRAND.line },
          },
          '& .MuiNativeSelect-select': { py: 1.25 },
        }}
      >
        {projectsLoading ? (
          <option value="" disabled>
            Loading projects…
          </option>
        ) : projects.length === 0 ? (
          <option value="" disabled>
            No projects found
          </option>
        ) : (
          <>
            <option value="" disabled={projectId != null}>
              Select a project
            </option>
            {projects.map((project) => (
              <option key={project.id} value={String(project.id)}>
                {project.name}
              </option>
            ))}
          </>
        )}
      </TextField>
      )}
      
      <TextField
        label="Swagger/OpenAPI URL"
        value={swaggerUrl}
        onChange={(e) => {
          setSwaggerUrl(e.target.value);
          if (swaggerFile) {
            setSwaggerFile(null);
          }
        }}
        fullWidth
        required={!hasFile}
        disabled={hasFile}
        placeholder="http://localhost:8080/api/v3/api-docs"
        error={!!urlError}
        helperText={hasFile ? 'Clear the selected file to import from a URL instead.' : urlError}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            minHeight: 40,
            '& fieldset': { borderColor: DFT_BRAND.line },
          },
        }}
      />

      <Box>
        <Button
          component="label"
          variant="outlined"
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            height: 36,
            minHeight: 36,
            borderRadius: '10px',
            color: DFT_BRAND.link,
            borderColor: DFT_BRAND.line,
            bgcolor: '#fff',
            boxShadow: 'none',
            whiteSpace: 'nowrap',
            '&:hover': {
              borderColor: '#93c5fd',
              bgcolor: DFT_BRAND.tint,
              boxShadow: 'none',
            },
          }}
        >
          Choose OpenAPI File
          <input
            type="file"
            accept=".json,.yaml,.yml,.xml,application/json,application/x-yaml,text/yaml,application/xml,text/xml"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setSwaggerFile(file);
              if (file) {
                setSwaggerUrl('');
              }
              e.target.value = '';
            }}
          />
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: DFT_BRAND.text,
              fontSize: '0.875rem',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {swaggerFile ? swaggerFile.name : 'No file selected'}
          </Typography>
          {swaggerFile && (
            <Tooltip title="Remove selected file">
              <IconButton
                size="small"
                onClick={() => setSwaggerFile(null)}
                disabled={loading}
                aria-label="Remove selected OpenAPI file"
                sx={{
                  color: 'error.main',
                  p: 0.25,
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'error.50' },
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography sx={{ display: 'block', mt: 0.5, color: DFT_BRAND.muted, fontSize: '0.8125rem' }}>
          Supports JSON and YAML OpenAPI/Swagger files.
        </Typography>
      </Box>

      {/* Environment Detection */}
      {environmentInfo && (
        <Card
          variant="outlined"
          sx={{
            mt: 0.5,
            borderRadius: '14px !important',
            borderColor: DFT_BRAND.line,
            boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
          }}
        >
          <CardContent sx={{ p: '12px !important' }}>
            <Typography sx={{ fontWeight: 700, mb: 1, color: DFT_BRAND.text, fontSize: '0.875rem' }}>
              Environment Detection
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Name:</Typography>
                <Typography variant="body2" fontWeight="medium">{environmentInfo.name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Type:</Typography>
                <Chip 
                  label={environmentInfo.environmentType} 
                  size="small" 
                  color={
                    environmentInfo.environmentType === 'Production' ? 'error' :
                    environmentInfo.environmentType === 'Staging' ? 'warning' :
                    environmentInfo.environmentType === 'Development' ? 'info' :
                    'default'
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">Base URL:</Typography>
                <Typography variant="body2" fontFamily="monospace" sx={{ minWidth: 0, wordBreak: 'break-all' }}>{environmentInfo.baseUrl}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Description:</Typography>
                <Typography variant="body2">{environmentInfo.description}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Environment Creation Toggle */}
      {environmentInfo && (
        <FormControlLabel
          control={
            <Switch
              checked={createEnvironment}
              onChange={(e) => setCreateEnvironment(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
              Assign imported requests to environment (creates or reuses by base URL)
            </Typography>
          }
          sx={{ alignItems: 'flex-start', mr: 0, ml: 0 }}
        />
      )}

      {error && <Alert severity="error">{error}</Alert>}
      
      <Button
        variant="contained"
        onClick={onSubmit}
        disabled={!canSubmit}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          fontWeight: 600,
          height: 36,
          minHeight: 36,
          minWidth: 88,
          px: 1.5,
          borderRadius: '10px',
          fontSize: '0.875rem',
          color: '#fff',
          bgcolor: '#1d4ed8',
          boxShadow: 'none',
          '&:hover': {
            bgcolor: '#1e40af',
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            color: '#fff',
            opacity: 0.55,
          },
        }}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : 'Import'}
      </Button>
    </Box>
  );
};

export default ImportSwaggerForm; 